import * as React from 'react';
import {Box, Tab, Tabs, useMediaQuery, useTheme} from '@mui/material';
import {alpha, styled} from '@mui/material/styles';
import {Menu} from '@base-ui/react/menu';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PayrollPeriods from './payrollperiods/PayrollPeriod';
import PayrollRun from './payrollperiods/PayrollRun';
import MonthlyChange from './monthlychanges/MonthlyChange';
import WorkEvidence from '../WorkEvidence/WorkEvidence';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../../context/AuthContext';
import {hasRole} from '../../utils/jwtHelper';

import type {PayrollTabKey} from './PayrollTabContext';

export type {PayrollTabKey};

interface TabPanelProps {
    children?: React.ReactNode;
    tabKey: PayrollTabKey;
    value: PayrollTabKey;
}

function CustomTabPanel(props: Readonly<TabPanelProps>) {
    const {children, value, tabKey, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== tabKey}
            id={`simple-tabpanel-${tabKey}`}
            aria-labelledby={`simple-tab-${tabKey}`}
            {...other}
        >
            {value === tabKey && <Box sx={{p: 0}}>{children}</Box>}
        </div>
    );
}

function a11yProps(tabKey: PayrollTabKey) {
    return {
        id: `simple-tab-${tabKey}`,
        'aria-controls': `simple-tabpanel-${tabKey}`,
    };
}

interface TabItem {
    key: PayrollTabKey;
    icon: React.ReactElement;
    label: string;
    shortLabel: string;
    content: React.ReactNode;
}

// ── Mobile menu-bar (@base-ui/react) — replaces the icon+label scrollable MUI Tabs on small
// screens, which wrapped/overflowed badly. Kept flush to the left (only small internal button
// padding) so it lines up with the page title above it instead of MUI Tabs' wider indentation.
const MobileMenuTrigger = styled(Menu.Trigger)(({theme}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1),
    marginLeft: theme.spacing(-1),
    border: 'none',
    borderRadius: 8,
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    font: 'inherit',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1.5,
    cursor: 'pointer',
    '&[data-popup-open]': {
        color: theme.palette.secondary.main,
    },
}));

const MobileMenuPositioner = styled(Menu.Positioner)(({theme}) => ({
    zIndex: theme.zIndex.modal + 1,
}));

const MobileMenuPopup = styled(Menu.Popup)(({theme}) => ({
    minWidth: 200,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[8],
    padding: theme.spacing(0.5),
    outline: 0,
}));

const MobileMenuItem = styled(Menu.Item)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    outline: 0,
    '&[data-highlighted]': {
        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
        color: theme.palette.secondary.main,
    },
}));

interface PayrollTabsProps {
    /** Controlled selected tab (with onTabChange). When omitted the component manages its own state. */
    readonly selectedTab?: PayrollTabKey;
    readonly onTabChange?: (tab: PayrollTabKey) => void;
    readonly initialTab?: PayrollTabKey;
    /** Rendered directly below the tab bar, above the tab content (e.g. the workflow steps card). */
    readonly belowTabs?: React.ReactNode;
}

export default function PayrollTabs({selectedTab, onTabChange, initialTab, belowTabs}: Readonly<PayrollTabsProps>): React.ReactElement {
    const {t} = useTranslation();
    const {token} = useAuth();
    const theme = useTheme();
    // Matches the breakpoint the rest of the page (PayrollPeriod/MonthlyChange mobile layouts)
    // switches on, so the tab switcher and the tab content change layout together.
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Check if user has adm_payroll role (not adm_branch_payroll)
    const canViewPayrollPeriods = hasRole(token, 'adm_payroll');

    // Order: Payroll Periods, Work Evidence, Monthly Changes, Payroll Run.
    const tabItems: TabItem[] = React.useMemo(() => {
        const items: TabItem[] = [];
        if (canViewPayrollPeriods) {
            items.push({
                key: 'periods', icon: <CalendarMonthIcon fontSize="small"/>,
                label: t('payroll.tabs.periods'), shortLabel: t('payroll.tabs.periodsShort'),
                content: <PayrollPeriods/>,
            });
        }
        items.push({
            key: 'workEvidence', icon: <FactCheckOutlinedIcon fontSize="small"/>,
            label: t('payroll.tabs.workEvidence'), shortLabel: t('payroll.tabs.workEvidenceShort'),
            content: <WorkEvidence/>,
        });
        items.push({
            key: 'changes', icon: <SwapHorizIcon fontSize="small"/>,
            label: t('payroll.tabs.changes'), shortLabel: t('payroll.tabs.changesShort'),
            content: <MonthlyChange/>,
        });
        if (canViewPayrollPeriods) {
            items.push({
                key: 'run', icon: <PlayCircleOutlineIcon fontSize="small"/>,
                label: t('payroll.tabs.run'), shortLabel: t('payroll.tabs.runShort'),
                content: <PayrollRun/>,
            });
        }
        return items;
    }, [canViewPayrollPeriods, t]);

    const [internalKey, setInternalKey] = React.useState<PayrollTabKey | undefined>(initialTab);
    const isControlled = onTabChange !== undefined;
    const selectedKey = isControlled ? selectedTab : internalKey;
    const setSelectedKey = (key: PayrollTabKey) => {
        if (!isControlled) setInternalKey(key);
        onTabChange?.(key);
    };
    // Fall back to the first visible tab when the requested one isn't available for this role.
    const activeTabItem = tabItems.find((item) => item.key === selectedKey) ?? tabItems[0];
    const value = activeTabItem.key;

    return (
        <>
            {isMobile ? (
                <Box sx={{mb: 1.5, borderBottom: 1, borderColor: 'divider', pb: 1, textAlign: 'left'}}>
                    <Menu.Root>
                        <MobileMenuTrigger>
                            {activeTabItem.icon}
                            {activeTabItem.shortLabel}
                            <ExpandMoreIcon fontSize="small"/>
                        </MobileMenuTrigger>
                        <Menu.Portal>
                            <MobileMenuPositioner align="start" sideOffset={6}>
                                <MobileMenuPopup>
                                    {tabItems.map((item) => (
                                        <MobileMenuItem key={item.key} onClick={() => setSelectedKey(item.key)}>
                                            {item.icon}
                                            {item.shortLabel}
                                        </MobileMenuItem>
                                    ))}
                                </MobileMenuPopup>
                            </MobileMenuPositioner>
                        </Menu.Portal>
                    </Menu.Root>
                </Box>
            ) : (
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={value} onChange={(_e, v: PayrollTabKey) => setSelectedKey(v)}
                          aria-label="payroll tabs" variant="scrollable" scrollButtons="auto">
                        {tabItems.map((item) => (
                            <Tab key={item.key} value={item.key} icon={item.icon} iconPosition="start"
                                 label={item.label} {...a11yProps(item.key)} />
                        ))}
                    </Tabs>
                </Box>
            )}
            {belowTabs && <Box sx={{mt: 2, '&:empty': {display: 'none'}}}>{belowTabs}</Box>}
            {tabItems.map((item) => (
                <CustomTabPanel key={item.key} value={value} tabKey={item.key}>
                    {item.content}
                </CustomTabPanel>
            ))}
        </>
    );
}

